import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';

type UseAsyncAnalysisLoaderArgs<TTarget, TData> = {
    load: (target: TTarget, args: { signal: AbortSignal }) => Promise<TData>;
    errorMessage: string;
};

type UseAsyncAnalysisLoaderValue<TTarget, TData> = {
    openedTarget: TTarget | null;
    openedTargetData: TData | null;
    isLoading: boolean;
    error: string | null;
    onOpen: (target: TTarget) => void;
    onClose: () => void;
};

const asyncAnalysisLoaderLogger = createClientLogger({
    scope: 'async-analysis-loader',
});

export const useAsyncAnalysisLoader = <TTarget, TData>({
    load,
    errorMessage,
}: UseAsyncAnalysisLoaderArgs<TTarget, TData>): UseAsyncAnalysisLoaderValue<TTarget, TData> => {
    const [openedTarget, setOpenedTarget] = useState<TTarget | null>(null);
    const [openedTargetData, setOpenedTargetData] = useState<TData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const abortCurrentRequest = useCallback(() => {
        if (!abortControllerRef.current) {
            return;
        }

        abortControllerRef.current.abort();
        abortControllerRef.current = null;
    }, []);

    useEffect(() => () => abortCurrentRequest(), [abortCurrentRequest]);

    const onClose = useCallback(() => {
        abortCurrentRequest();
        setOpenedTarget(null);
        setOpenedTargetData(null);
        setError(null);
        setIsLoading(false);
    }, [abortCurrentRequest]);

    const onOpen = useCallback(
        (target: TTarget) => {
            abortCurrentRequest();

            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            setOpenedTarget(target);
            setOpenedTargetData(null);
            setError(null);
            setIsLoading(true);

            void load(target, { signal: abortController.signal })
                .then((data) => {
                    setOpenedTargetData(data);
                })
                .catch((requestError) => {
                    if (isRequestCanceled(requestError)) {
                        return;
                    }

                    logClientError({
                        logger: asyncAnalysisLoaderLogger,
                        message: '[client.product-analysis.async-load] Failed to load async analysis payload.',
                        error: requestError,
                    });
                    setError(errorMessage);
                })
                .finally(() => {
                    if (abortControllerRef.current === abortController) {
                        abortControllerRef.current = null;
                        setIsLoading(false);
                    }
                });
        },
        [abortCurrentRequest, errorMessage, load],
    );

    return {
        openedTarget,
        openedTargetData,
        isLoading,
        error,
        onOpen,
        onClose,
    };
};
